'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import { useTheme } from 'next-themes';
import { useAuth } from '@/lib/auth';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  Sun,
  Moon,
  Menu,
  Home,
  User as UserIcon,
  LogOut,
  LayoutDashboard,
  Briefcase,
  Calendar,
  HeartHandshake,
  Users,
  GraduationCap,
  Shield,
  Settings,
  ChevronRight,
  ChevronDown,
  Database,
  Newspaper,
} from 'lucide-react';
import { NotificationBell } from '@/components/common/NotificationBell';

export function Navbar() {
  const pathname = usePathname();
  const router = useRouter();
  const { theme, setTheme } = useTheme();
  const { user, isAuthenticated, logout, isAdmin } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [
    { href: '/', label: 'Beranda', shortLabel: 'Beranda', icon: Home },
    { href: '/alumni', label: 'Direktori Alumni', shortLabel: 'Direktori', icon: Users },
    { href: '/berita', label: 'Kabar & Artikel', shortLabel: 'Berita', icon: Newspaper },
    { href: '/tracer-study', label: 'Tracer Study', shortLabel: 'Tracer Study', icon: GraduationCap },
    { href: '/karir', label: 'Bursa Karir', shortLabel: 'Bursa Karir', icon: Briefcase },
    { href: '/kegiatan', label: 'Agenda & Event', shortLabel: 'Agenda', icon: Calendar },
    ...(user ? [{ href: '/donasi', label: 'Donasi & Sinergi', shortLabel: 'Donasi', icon: HeartHandshake }] : []),
  ];

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  return (
    <header className="sticky top-0 z-50 w-full transition-all">
      {/* Top Corporate Bar - Dark Blue #082B52 */}
      <div className="w-full bg-[#082B52] text-white border-b-2 border-[#D71920]">
        <div className="w-full max-w-[1520px] mx-auto flex h-9 items-center justify-between px-4 sm:px-6 lg:px-8 text-xs">
          <div className="flex items-center gap-2 truncate">
            <span className="inline-block h-2 w-2 rounded-full bg-[#D71920]" />
            <span className="font-semibold tracking-wider text-slate-200 uppercase text-[11px] sm:text-xs truncate">
              Ikatan Keluarga Alumni Teknik Mesin ITN Malang
            </span>
            <span className="hidden md:inline text-slate-400">•</span>
            <span className="hidden md:inline font-bold text-[#EF4444] text-[11px] uppercase tracking-wide">
              Solidarity Forever
            </span>
          </div>
          <div className="hidden sm:flex items-center gap-4 text-slate-300 text-[11px]">
            <span>Akreditasi Unggul</span>
            <span>•</span>
            <span className="text-slate-400">Kampus II ITN Malang</span>
          </div>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <div className="w-full border-b border-[#E2E8F0] dark:border-slate-800 bg-white/95 dark:bg-[#0A2544]/95 backdrop-blur-md shadow-xs">
        <div className="w-full max-w-[1520px] mx-auto flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* Brand Identity / Logo */}
          <Link href="/" className="flex items-center gap-2.5 xl:gap-3 group focus:outline-none shrink-0">
            <div className="relative flex h-10 w-10 xl:h-11 xl:w-11 shrink-0 items-center justify-center rounded-xl bg-white p-1 border border-[#E2E8F0] shadow-xs group-hover:border-[#004A99] group-hover:shadow-sm transition-all overflow-hidden">
              <Image
                src="/images/logo-ika-mesin.png"
                alt="Logo IKA MESIN ITN MALANG"
                width={38}
                height={38}
                className="object-contain"
                priority
              />
            </div>
            <div className="flex flex-col justify-center">
              <div className="flex items-center gap-1.5 xl:gap-2 whitespace-nowrap">
                <span className="font-extrabold tracking-tight text-base xl:text-lg text-[#082B52] dark:text-white leading-none whitespace-nowrap">
                  IKA MESIN
                </span>
                <span className="rounded bg-[#004A99] px-1.5 py-0.5 text-[9px] font-black text-white uppercase tracking-wider whitespace-nowrap shadow-2xs">
                  ITN MALANG
                </span>
              </div>
              <span className="text-[9px] xl:text-[10px] font-extrabold tracking-wider text-[#D71920] uppercase mt-1 leading-none whitespace-nowrap">
                Solidarity Forever
              </span>
            </div>
          </Link>

          {/* Architectural Hairline Divider between Brand & Nav Links */}
          <div className="hidden lg:block h-8 w-px bg-slate-200 dark:bg-slate-700 mx-3 xl:mx-6 shrink-0" />

          {/* Desktop Navigation Links */}
          <nav className="hidden lg:flex items-center gap-1 xl:gap-2 shrink-0">
            {navLinks.map((link) => {
              const active = isActive(link.href);
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`inline-flex items-center justify-center h-9 px-2.5 xl:px-3.5 text-xs xl:text-[13px] rounded-lg transition-all whitespace-nowrap shrink-0 ${
                    active
                      ? 'text-[#004A99] dark:text-blue-400 bg-blue-50/90 dark:bg-blue-950/60 font-bold border border-blue-200/60 dark:border-blue-900/50 shadow-xs'
                      : 'text-[#334155] dark:text-slate-200 font-semibold hover:text-[#004A99] dark:hover:text-white hover:bg-slate-100/80 dark:hover:bg-slate-800/60'
                  }`}
                >
                  <span className="hidden xl:inline">{link.label}</span>
                  <span className="xl:hidden">{link.shortLabel}</span>
                </Link>
              );
            })}
          </nav>

          {/* Right Section: Theme Toggle, Notifications, Auth */}
          <div className="hidden lg:flex items-center gap-2 xl:gap-2.5 shrink-0">
            {/* Subtle Divider */}
            <div className="h-6 w-px bg-slate-200 dark:bg-slate-700 hidden lg:block mx-0.5 shrink-0" />

            {/* Theme Switcher */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              className="rounded-lg text-[#64748B] hover:text-[#082B52] dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 h-9 w-9 shrink-0"
              title="Ganti Tema"
            >
              <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
              <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
              <span className="sr-only">Ganti Tema</span>
            </Button>

            {/* Notification Bell */}
            <NotificationBell />

            {/* User Profile or Login/Register Buttons */}
            {isAuthenticated && user ? (
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-2 rounded-lg border border-[#E2E8F0] dark:border-slate-700 bg-white dark:bg-slate-800 pl-1.5 pr-2 py-1 hover:border-[#004A99] text-left transition-colors outline-none cursor-pointer shadow-xs shrink-0">
                  <div className="flex h-7 w-7 items-center justify-center rounded-md bg-gradient-to-br from-[#004A99] to-[#082B52] text-white font-bold text-xs shrink-0 shadow-xs">
                    {user.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div className="hidden xl:flex flex-col text-left overflow-hidden">
                    <span className="text-xs font-bold max-w-[120px] truncate leading-tight text-[#082B52] dark:text-white">
                      {user.name}
                    </span>
                    <span className="text-[10px] text-[#64748B] dark:text-slate-400 leading-tight truncate capitalize">
                      {user.roles?.[0] || user.user_type || 'Alumni'}
                    </span>
                  </div>
                  <ChevronDown className="h-3.5 w-3.5 text-[#64748B] shrink-0" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 rounded-lg p-1.5 shadow-lg border border-[#E2E8F0]">
                  <DropdownMenuLabel className="font-normal px-2.5 py-2">
                    <div className="flex flex-col space-y-1">
                      <p className="text-xs font-bold text-[#082B52] dark:text-white">{user.name}</p>
                      <p className="text-[11px] text-[#64748B] truncate">{user.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => router.push('/dashboard')}
                    className="cursor-pointer rounded-md flex items-center gap-2 text-xs font-medium py-2"
                  >
                    <LayoutDashboard className="h-4 w-4 text-[#004A99]" />
                    <span>Dashboard Alumni</span>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuItem
                        onClick={() => router.push('/admin')}
                        className="cursor-pointer rounded-md flex items-center gap-2 text-xs font-bold text-[#D71920] py-2"
                      >
                        <Shield className="h-4 w-4" />
                        <span>Panel Administrator</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => router.push('/admin/profile')}
                        className="cursor-pointer rounded-md flex items-center gap-2 text-xs font-medium py-2"
                      >
                        <Settings className="h-4 w-4 text-[#004A99]" />
                        <span>Pengaturan Admin</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => router.push('/admin?tab=backup')}
                        className="cursor-pointer rounded-md flex items-center gap-2 text-xs font-bold text-emerald-700 dark:text-emerald-400 py-2 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                      >
                        <Database className="h-4 w-4 text-emerald-600" />
                        <span>Backup Database (.CSV / Email)</span>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => logout()}
                    className="cursor-pointer rounded-md text-[#D71920] focus:text-[#D71920] focus:bg-red-50 dark:focus:bg-red-950/30 flex items-center text-xs font-medium py-2"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    <span>Keluar</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2 xl:gap-2.5 shrink-0">
                <Link
                  href="/login"
                  className={buttonVariants({
                    variant: 'outline',
                    className:
                      'rounded-md border-[#004A99]/30 text-[#004A99] dark:text-blue-400 hover:bg-blue-50/70 dark:hover:bg-blue-950/40 font-bold text-xs h-9 px-3.5 whitespace-nowrap',
                  })}
                >
                  Masuk
                </Link>
                <Link
                  href="/register"
                  className={buttonVariants({
                    className:
                      'rounded-md bg-[#D71920] hover:bg-[#B8141B] text-white font-bold text-xs h-9 px-4 shadow-sm transition-colors whitespace-nowrap',
                  })}
                >
                  Daftar Alumni
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Trigger & Theme Switcher */}
          <div className="flex lg:hidden items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              className="rounded-lg text-[#64748B] h-9 w-9"
            >
              <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
              <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
            </Button>

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger
                className="inline-flex items-center justify-center rounded-lg border border-[#E2E8F0] text-[#082B52] dark:text-white h-9 w-9 hover:bg-slate-100 transition-colors cursor-pointer"
                aria-label="Buka Menu"
              >
                <Menu className="h-5 w-5" />
              </SheetTrigger>
              <SheetContent side="right" className="w-80 p-0 flex flex-col bg-white dark:bg-[#0A2544]">
                <SheetHeader className="p-4 border-b border-[#E2E8F0] dark:border-slate-800 text-left bg-[#082B52] text-white">
                  <SheetTitle className="text-base font-bold text-white flex items-center gap-2.5">
                    <Image
                      src="/images/logo-ika-mesin.png"
                      alt="Logo"
                      width={32}
                      height={32}
                      className="rounded bg-white p-0.5"
                    />
                    <div>
                      <div className="text-sm font-extrabold tracking-tight">IKA MESIN ITN MALANG</div>
                      <div className="text-[10px] font-bold text-[#EF4444] uppercase tracking-wider">
                        Solidarity Forever
                      </div>
                    </div>
                  </SheetTitle>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {/* Category 1: Navigasi Utama */}
                  <div>
                    <div className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider px-3 mb-1.5">
                      Navigasi Utama
                    </div>
                    <div className="space-y-1">
                      {[
                        { href: '/', label: 'Beranda', desc: 'Halaman utama portal', icon: Home },
                        { href: '/alumni', label: 'Direktori Alumni', desc: 'Database seluruh angkatan', icon: Users },
                        { href: '/tracer-study', label: 'Tracer Study', desc: 'Pelacakan jejak & akreditasi', icon: GraduationCap },
                      ].map((item) => {
                        const active = isActive(item.href);
                        const Icon = item.icon;
                        return (
                          <Link
                            key={item.href}
                            href={item.href}
                            onClick={() => setMobileOpen(false)}
                            className={`flex items-center justify-between p-2.5 rounded-lg transition-colors ${
                              active
                                ? 'bg-blue-50 text-[#004A99] dark:bg-blue-950/60 dark:text-blue-300 font-bold'
                                : 'text-[#1F2937] dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
                            }`}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <div className={`p-2 rounded-md shrink-0 ${active ? 'bg-[#004A99] text-white' : 'bg-slate-100 dark:bg-slate-800 text-[#004A99]'}`}>
                                <Icon className="h-4 w-4" />
                              </div>
                              <div className="min-w-0">
                                <div className="text-xs font-bold leading-tight truncate">{item.label}</div>
                                <div className="text-[10px] text-[#64748B] dark:text-slate-400 leading-tight mt-0.5 truncate">{item.desc}</div>
                              </div>
                            </div>
                            <ChevronRight className="h-4 w-4 text-[#94A3B8] shrink-0" />
                          </Link>
                        );
                      })}
                    </div>
                  </div>

                  {/* Category 2: Karir & Sinergi */}
                  <div>
                    <div className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider px-3 mb-1.5">
                      Karir & Sinergi Alumni
                    </div>
                    <div className="space-y-1">
                      {[
                        { href: '/karir', label: 'Bursa Karir', desc: 'Peluang kerja & magang industri', icon: Briefcase },
                        { href: '/kegiatan', label: 'Agenda & Event', desc: 'Temu kangen & reuni akbar', icon: Calendar },
                        { href: '/donasi', label: 'Donasi & Sinergi', desc: 'Dana abadi & beasiswa', icon: HeartHandshake },
                      ].map((item) => {
                        const active = isActive(item.href);
                        const Icon = item.icon;
                        return (
                          <Link
                            key={item.href}
                            href={item.href}
                            onClick={() => setMobileOpen(false)}
                            className={`flex items-center justify-between p-2.5 rounded-lg transition-colors ${
                              active
                                ? 'bg-blue-50 text-[#004A99] dark:bg-blue-950/60 dark:text-blue-300 font-bold'
                                : 'text-[#1F2937] dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
                            }`}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <div className={`p-2 rounded-md shrink-0 ${active ? 'bg-[#004A99] text-white' : 'bg-slate-100 dark:bg-slate-800 text-[#004A99]'}`}>
                                <Icon className="h-4 w-4" />
                              </div>
                              <div className="min-w-0">
                                <div className="text-xs font-bold leading-tight truncate">{item.label}</div>
                                <div className="text-[10px] text-[#64748B] dark:text-slate-400 leading-tight mt-0.5 truncate">{item.desc}</div>
                              </div>
                            </div>
                            <ChevronRight className="h-4 w-4 text-[#94A3B8] shrink-0" />
                          </Link>
                        );
                      })}
                    </div>
                  </div>

                  {isAuthenticated && isAdmin && (
                    <div className="pt-2 border-t border-[#E2E8F0] dark:border-slate-800">
                      <div className="text-[10px] font-bold text-[#D71920] uppercase tracking-wider px-3 mb-1.5">
                        Administrasi Pengurus
                      </div>
                      <Link
                        href="/admin"
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-3 p-2.5 rounded-lg text-xs font-bold text-[#D71920] hover:bg-red-50 dark:hover:bg-red-950/20"
                      >
                        <div className="p-2 rounded-md bg-red-100 text-[#D71920]">
                          <Shield className="h-4 w-4" />
                        </div>
                        <div>
                          <div>Panel Administrator</div>
                          <div className="text-[10px] text-[#64748B] font-normal">Kelola verifikasi alumni & data</div>
                        </div>
                      </Link>
                    </div>
                  )}
                </div>

                <div className="p-4 border-t border-[#E2E8F0] dark:border-slate-800 bg-[#F5F7FA] dark:bg-slate-900/50">
                  {isAuthenticated && user ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#004A99] text-white font-bold text-sm">
                          {user.name?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div className="overflow-hidden">
                          <div className="text-xs font-bold text-[#082B52] dark:text-white truncate">
                            {user.name}
                          </div>
                          <div className="text-[11px] text-[#64748B] truncate">{user.email}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          asChild
                          variant="outline"
                          size="sm"
                          className="w-full text-xs font-bold"
                          onClick={() => setMobileOpen(false)}
                        >
                          <Link href="/dashboard">Dashboard</Link>
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          className="w-full text-xs font-bold bg-[#D71920] hover:bg-[#B8141B]"
                          onClick={() => {
                            setMobileOpen(false);
                            logout();
                          }}
                        >
                          Keluar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        asChild
                        variant="outline"
                        size="sm"
                        className="w-full border-[#004A99]/30 text-[#004A99] font-bold text-xs"
                        onClick={() => setMobileOpen(false)}
                      >
                        <Link href="/login">Masuk</Link>
                      </Button>
                      <Button
                        asChild
                        size="sm"
                        className="w-full bg-[#D71920] hover:bg-[#B8141B] text-white font-bold text-xs"
                        onClick={() => setMobileOpen(false)}
                      >
                        <Link href="/register">Daftar</Link>
                      </Button>
                    </div>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
